import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Image as ImageIcon, Scan, Sparkle, DownloadSimple, UploadSimple, FloppyDisk, CheckSquare, Square, Camera, ArrowClockwise, TextT, FilePdf, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

// Which page/tab is shown
const TABS = [
  { id: "photo",    label: "AI Photo Studio",   icon: Sparkle },
  { id: "scan",     label: "Scan Center · OCR", icon: Scan },
  { id: "passport", label: "Passport Layout",   icon: ImageIcon },
  { id: "bulk",     label: "Bulk Enhance",      icon: UploadSimple },
];

export default function PhotoScanStudio() {
  const [searchParams] = useSearchParams();
  const initial = searchParams.get("tab");
  const [tab, setTab] = useState(TABS.some((t) => t.id === initial) ? initial : "photo");

  return (
    <div className="max-w-[1500px] mx-auto p-6" data-testid="photo-scan-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={14} weight="fill" className="text-orange-500" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">AI Photo & Scan Studio</span>
        </div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Photo · Scan · OCR</h1>
        <p className="text-sm text-slate-500 mt-1">Background removal · HD Upscale · Face restore · Auto-crop scan · Multi-language OCR (English / Hindi / Marathi).</p>
      </div>

      <div className="flex gap-2 mb-6" data-testid="ps-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`ps-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-medium transition-colors ${tab === t.id ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-100"}`}
          >
            <t.icon size={14} weight={tab === t.id ? "fill" : "regular"} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "photo" && <PhotoStudio />}
      {tab === "scan"  && <ScanCenter />}
      {tab === "passport" && <PassportLayout />}
      {tab === "bulk"     && <BulkEnhance />}
    </div>
  );
}

// ============================================================
// Passport Photo Layout
// ============================================================
function PassportLayout() {
  const [source, setSource] = useState("");
  const [paper, setPaper] = useState("A4");
  const [count, setCount] = useState(8);
  const [photoSize, setPhotoSize] = useState("passport");
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState("");
  const [info, setInfo] = useState(null);
  const fileRef = useRef(null);

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 10_000_000) { toast.error("Max 10 MB"); return; }
    const r = new FileReader();
    r.onload = () => { setSource(r.result); setOutput(""); };
    r.readAsDataURL(file);
  };

  const run = async () => {
    if (!source) { toast.error("Pick a photo"); return; }
    setBusy(true); setOutput("");
    try {
      const { data } = await api.post("/photo/passport-layout", {
        image: source, paper, count: Number(count), photo_size: photoSize, dpi: 300,
      });
      setOutput(data.image);
      setInfo({ cols: data.cols, rows: data.rows, photos: data.photos, dpi: data.dpi });
      toast.success(`${data.photos} photos ready on ${paper}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const download = () => {
    if (!output) return;
    const a = document.createElement("a");
    a.href = output; a.download = `passport-${paper}-${count}up.png`; a.click();
  };

  const printSheet = () => {
    if (!output) return;
    const w = window.open("", "_blank"); if (!w) { toast.error("Popup blocked"); return; }
    w.document.write(`<!doctype html><html><head><title>Passport Sheet</title>
      <style>@page { size: ${paper === "A4" ? "A4" : "152.4mm 101.6mm"}; margin: 0; } body { margin:0; } img { width: 100%; display: block; }</style>
      </head><body><img src="${output}" onload="setTimeout(()=>window.print(),300)"/></body></html>`);
    w.document.close();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <Card className="lg:col-span-1 p-5 space-y-4 bg-white border-slate-200">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Photo</Label>
          <input ref={fileRef} type="file" accept="image/*" data-testid="pp-file" onChange={(e) => onFile(e.target.files?.[0])}
            className="text-xs mt-1 file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-orange-600 file:text-white" />
        </div>
        <div>
          <Label>Paper</Label>
          <Select value={paper} onValueChange={setPaper}>
            <SelectTrigger data-testid="pp-paper"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="A4">A4 (210×297mm)</SelectItem><SelectItem value="4x6">4×6 photo (152×102mm)</SelectItem></SelectContent>
          </Select>
        </div>
        <div>
          <Label>Photos per sheet</Label>
          <Select value={String(count)} onValueChange={(v) => setCount(Number(v))}>
            <SelectTrigger data-testid="pp-count"><SelectValue /></SelectTrigger>
            <SelectContent>{[2, 4, 6, 8, 12, 16].map((n) => <SelectItem key={n} value={String(n)}>{n} photos</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Photo size</Label>
          <Select value={photoSize} onValueChange={setPhotoSize}>
            <SelectTrigger data-testid="pp-photo-size"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="passport">Passport 35×45mm</SelectItem>
              <SelectItem value="stamp">Stamp 25×30mm</SelectItem>
              <SelectItem value="visa">Visa 51×51mm</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={busy || !source} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="pp-run">
          {busy ? "Composing…" : "Compose Sheet"}
        </Button>
        {output && (
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={download} variant="outline" size="sm" className="gap-1 text-xs" data-testid="pp-download"><DownloadSimple size={12} /> Download PNG</Button>
            <Button onClick={printSheet} variant="outline" size="sm" className="gap-1 text-xs" data-testid="pp-print">Print</Button>
          </div>
        )}
        {info && (
          <div className="text-[10px] text-slate-500 italic">
            Grid: {info.cols} × {info.rows} = {info.photos} photos · {info.dpi} DPI · Cut on the light gutters between photos.
          </div>
        )}
      </Card>

      <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 bg-slate-50 border-slate-200 border-dashed">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Source Photo</div>
          {source ? <img src={source} alt="src" className="w-full max-h-[520px] object-contain rounded-md bg-white" /> : (
            <div className="w-full h-[520px] rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400">
              <UploadSimple size={38} weight="duotone" className="mb-2" />
              <div className="text-sm">Upload a portrait photo</div>
            </div>
          )}
        </Card>
        <Card className="p-4 bg-white border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Composed Sheet</div>
          {output ? <img src={output} alt="sheet" className="w-full max-h-[520px] object-contain rounded-md bg-slate-100 border border-slate-200" data-testid="pp-output" /> : (
            <div className="w-full h-[520px] rounded-md border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 text-sm">
              The tiled sheet appears here.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ============================================================
// Bulk Photo Enhance
// ============================================================
function BulkEnhance() {
  const [images, setImages] = useState([]); // [{ name, size, b64 }]
  const [tool, setTool] = useState("bg_remove");
  const [tools, setTools] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]); // [{ index, ok, output_url, error }]
  const fileRef = useRef(null);

  useEffect(() => { api.get("/photo/tools").then((r) => setTools(r.data)).catch(() => {}); }, []);

  const add = async (fl) => {
    const arr = Array.from(fl || []).slice(0, 20 - images.length);
    const loaded = await Promise.all(arr.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res({ name: f.name, size: f.size, b64: r.result }); r.readAsDataURL(f);
    })));
    setImages((c) => [...c, ...loaded].slice(0, 20));
  };

  const run = async () => {
    if (!images.length) return;
    setBusy(true); setResults([]); setProgress(0);
    try {
      const { data } = await api.post("/photo/enhance-bulk", { tool, images: images.map((i) => i.b64) });
      setResults(data.results);
      toast.success(`${data.success}/${data.results.length} enhanced`);
      setProgress(100);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadZip = async () => {
    const okResults = results.filter((r) => r.ok);
    if (okResults.length === 0) { toast.error("Nothing to download"); return; }
    const JSZip = (await import("jszip")).default;
    const zip = new JSZip();
    for (const r of okResults) {
      try {
        const resp = await fetch(r.output_url);
        const buf = await resp.arrayBuffer();
        const name = images[r.index]?.name?.replace(/\.[^.]+$/, "") || `photo-${r.index}`;
        zip.file(`${name}-${tool}.png`, buf);
      } catch {}
    }
    const blob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `enhanced-${tool}-${okResults.length}.zip`; a.click();
    URL.revokeObjectURL(url);
    toast.success("ZIP downloaded");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <Card className="lg:col-span-1 p-5 space-y-4 bg-white border-slate-200">
        <div>
          <Label>Tool</Label>
          <Select value={tool} onValueChange={setTool}>
            <SelectTrigger data-testid="bulk-tool"><SelectValue /></SelectTrigger>
            <SelectContent>{tools.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Images (up to 20)</Label>
          <input ref={fileRef} type="file" accept="image/*" multiple data-testid="bulk-file" onChange={(e) => add(e.target.files)}
            className="text-xs mt-1 file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-orange-600 file:text-white" />
          <div className="text-[10px] text-slate-500 mt-1">{images.length} / 20 selected · Runs 4 in parallel</div>
        </div>
        <Button onClick={run} disabled={busy || !images.length} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="bulk-run">
          {busy ? `Processing ${images.length}…` : `Enhance ${images.length} photos`}
        </Button>
        {results.length > 0 && (
          <Button onClick={downloadZip} variant="outline" className="w-full gap-2" data-testid="bulk-zip">
            <DownloadSimple size={14} /> Download ZIP ({results.filter((r) => r.ok).length})
          </Button>
        )}
        <div className="text-[10px] text-slate-500 italic">Powered by fal.ai · charged per image</div>
      </Card>

      <div className="lg:col-span-3">
        <Card className="p-4 bg-white border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Batch Preview</div>
          {images.length === 0 && <div className="text-center py-16 text-slate-400 text-sm">Add up to 20 photos to enhance them all in one batch.</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {images.map((im, i) => {
              const r = results.find((x) => x.index === i);
              return (
                <div key={i} className="relative border border-slate-200 rounded-md overflow-hidden" data-testid={`bulk-item-${i}`}>
                  <img src={r?.output_url || im.b64} alt="" className="w-full h-40 object-cover bg-slate-50" />
                  <div className="absolute top-1 right-1">
                    {r ? (
                      r.ok ? <Badge className="text-[9px] bg-emerald-600 text-white">✓ done</Badge> : <Badge className="text-[9px] bg-red-600 text-white">error</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[9px] bg-white">pending</Badge>
                    )}
                  </div>
                  <div className="text-[10px] p-1.5 truncate">{im.name}</div>
                  {!r && !busy && (
                    <button onClick={() => setImages((c) => c.filter((_, j) => j !== i))} className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">×</button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}

// ============================================================
// AI Photo Studio
// ============================================================
function PhotoStudio() {
  const [tools, setTools] = useState([]);
  const [tool, setTool] = useState("bg_remove");
  const [source, setSource] = useState("");       // data URL
  const [output, setOutput] = useState("");       // URL from fal.ai
  const [busy, setBusy] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("_none");
  const [saveDoc, setSaveDoc] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/photo/tools").then((r) => setTools(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 10_000_000) { toast.error("Max 10 MB image"); return; }
    const r = new FileReader();
    r.onload = () => { setSource(r.result); setOutput(""); };
    r.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    onFile(e.dataTransfer.files?.[0]);
  };
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type?.startsWith("image/")) { onFile(it.getAsFile()); break; }
    }
  };
  useEffect(() => {
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const run = async () => {
    if (!source) { toast.error("Choose an image first"); return; }
    setBusy(true); setOutput("");
    try {
      const { data } = await api.post("/photo/enhance", {
        tool,
        image_base64: source,
        customer_id: (saveDoc && customerId !== "_none") ? customerId : "",
        save_as_document: saveDoc && customerId !== "_none",
      });
      setOutput(data.output_url);
      toast.success(saveDoc && data.document_id ? "Enhanced · saved to customer profile" : "Enhanced");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI failed"); }
    finally { setBusy(false); }
  };

  const download = async () => {
    if (!output) return;
    try {
      const res = await fetch(output);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `photo-${tool}-${Date.now()}.png`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <Card className="lg:col-span-1 p-5 space-y-4 bg-white border-slate-200">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Enhancement Tool</Label>
          <Select value={tool} onValueChange={setTool}>
            <SelectTrigger className="mt-1" data-testid="ps-tool"><SelectValue /></SelectTrigger>
            <SelectContent>{tools.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Image</Label>
          <input ref={fileRef} type="file" accept="image/*" data-testid="ps-file" onChange={(e) => onFile(e.target.files?.[0])}
            className="text-xs mt-1 file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-orange-600 file:text-white" />
          <div className="text-[10px] text-slate-500 mt-1">Or paste (Ctrl+V) / drag &amp; drop / take camera photo below.</div>
          <input type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e.target.files?.[0])}
            className="text-xs mt-2 file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700" />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={saveDoc} onChange={(e) => setSaveDoc(e.target.checked)} data-testid="ps-save-doc" className="w-4 h-4" />
            Save output to customer profile
          </label>
          {saveDoc && (
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="_none">— Choose customer —</SelectItem>
                {customers.slice(0, 300).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        <Button onClick={run} disabled={busy || !source} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="ps-run">
          <Sparkle size={14} weight="fill" /> {busy ? "AI thinking… (~10s)" : "Enhance with AI"}
        </Button>
        <div className="text-[10px] text-slate-500 italic text-center">Powered by fal.ai · costs a few ₹ per image</div>
      </Card>

      <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 bg-slate-50 border-slate-200 border-dashed" onDrop={onDrop} onDragOver={(e) => e.preventDefault()} data-testid="ps-source-panel">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Source</div>
          {source ? (
            <img src={source} alt="source" className="w-full max-h-[520px] object-contain rounded-md bg-white" data-testid="ps-source-img" />
          ) : (
            <button onClick={() => fileRef.current?.click()} className="w-full h-[520px] rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-orange-400 hover:text-orange-500 transition-colors">
              <UploadSimple size={38} weight="duotone" className="mb-2" />
              <div className="text-sm font-medium">Drop image · Click to browse · Paste Ctrl+V</div>
              <div className="text-xs mt-1">JPG / PNG / WEBP · up to 10 MB</div>
            </button>
          )}
        </Card>

        <Card className="p-4 bg-white border-slate-200" data-testid="ps-output-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Enhanced Output</div>
            {output && <Button size="sm" variant="outline" onClick={download} className="gap-1.5 text-xs h-8" data-testid="ps-download"><DownloadSimple size={12} /> Download</Button>}
          </div>
          {output ? (
            <div className="rounded-md bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAyMCAyMCcgZmlsbD0nI2VlZScgd2lkdGg9JzE2JyBoZWlnaHQ9JzE2Jz48cmVjdCB4PScwJyB5PScwJyB3aWR0aD0nMTAnIGhlaWdodD0nMTAnLz48cmVjdCB4PScxMCcgeT0nMTAnIHdpZHRoPScxMCcgaGVpZ2h0PScxMCcvPjwvc3ZnPg==')]">
              <img src={output} alt="output" className="w-full max-h-[520px] object-contain" data-testid="ps-output-img" />
            </div>
          ) : (
            <div className="w-full h-[520px] rounded-md border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400">
              <Sparkle size={38} weight="duotone" className="mb-2 opacity-50" />
              <div className="text-sm">The enhanced image will appear here.</div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ============================================================
// Scan Center · Tesseract OCR
// ============================================================
function ScanCenter() {
  const [languages, setLanguages] = useState([]);
  const [langSel, setLangSel] = useState(["eng", "hin", "mar"]);
  const [source, setSource] = useState("");
  const [processed, setProcessed] = useState("");
  const [pdf, setPdf] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [autoCrop, setAutoCrop] = useState(true);
  const [deskew, setDeskew] = useState(true);
  const [threshold, setThreshold] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/scan/languages").then((r) => {
      setLanguages(r.data);
      // Only select installed languages
      const installed = new Set(r.data.map((l) => l.id));
      setLangSel(["eng", "hin", "mar"].filter((l) => installed.has(l)));
    }).catch(() => {});
  }, []);

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 10_000_000) { toast.error("Max 10 MB image"); return; }
    const r = new FileReader();
    r.onload = () => { setSource(r.result); setProcessed(""); setPdf(""); setText(""); };
    r.readAsDataURL(file);
  };

  const toggleLang = (id) =>
    setLangSel((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const run = async () => {
    if (!source) { toast.error("Choose an image first"); return; }
    if (langSel.length === 0) { toast.error("Pick at least one OCR language"); return; }
    setBusy(true); setProcessed(""); setPdf(""); setText("");
    try {
      const { data } = await api.post("/scan/ocr", {
        image_base64: source,
        languages: langSel.join("+"),
        auto_crop: autoCrop,
        deskew,
        threshold,
      });
      setProcessed(data.processed_image);
      setPdf(data.searchable_pdf);
      setText(data.text);
      toast.success(`OCR complete · ${data.text_length} chars extracted`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "OCR failed"); }
    finally { setBusy(false); }
  };

  const downloadPdf = () => {
    if (!pdf) return;
    const a = document.createElement("a");
    a.href = pdf; a.download = `scan-${Date.now()}.pdf`; a.click();
  };
  const downloadImg = () => {
    if (!processed) return;
    const a = document.createElement("a");
    a.href = processed; a.download = `scan-${Date.now()}.png`; a.click();
  };
  const copyText = () => {
    navigator.clipboard.writeText(text);
    toast.success("OCR text copied");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <Card className="lg:col-span-1 p-5 space-y-4 bg-white border-slate-200">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">OCR Languages</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="ps-lang-group">
            {languages.map((l) => (
              <button
                key={l.id}
                onClick={() => toggleLang(l.id)}
                data-testid={`ps-lang-${l.id}`}
                className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${langSel.includes(l.id) ? "border-orange-500 bg-orange-50 text-orange-800" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}
              >
                {langSel.includes(l.id) ? "✓ " : ""}{l.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Processing</Label>
          <div className="mt-2 space-y-1.5">
            <Toggle checked={autoCrop} onChange={setAutoCrop} label="Auto-crop document edges" testid="ps-auto-crop" />
            <Toggle checked={deskew} onChange={setDeskew} label="Deskew (straighten)" testid="ps-deskew" />
            <Toggle checked={threshold} onChange={setThreshold} label="Black & white (cleaner OCR)" testid="ps-threshold" />
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Image</Label>
          <input ref={fileRef} type="file" accept="image/*" data-testid="ps-scan-file" onChange={(e) => onFile(e.target.files?.[0])}
            className="text-xs mt-1 file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-orange-600 file:text-white" />
          <input type="file" accept="image/*" capture="environment" onChange={(e) => onFile(e.target.files?.[0])}
            className="text-xs mt-2 file:mr-2 file:py-1.5 file:px-2 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700" />
        </div>

        <Button onClick={run} disabled={busy || !source} className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white gap-2" data-testid="ps-scan-run">
          <Scan size={14} weight="fill" /> {busy ? "Scanning…" : "Scan & Extract Text"}
        </Button>
      </Card>

      <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 bg-slate-50 border-slate-200 border-dashed" data-testid="ps-scan-source">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Source Image</div>
          {source ? (
            <img src={source} alt="src" className="w-full max-h-[350px] object-contain rounded-md bg-white" />
          ) : (
            <button onClick={() => fileRef.current?.click()} className="w-full h-[350px] rounded-md border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-orange-400 hover:text-orange-500 transition-colors">
              <Camera size={38} weight="duotone" className="mb-2" />
              <div className="text-sm font-medium">Upload document photo</div>
            </button>
          )}
        </Card>

        <Card className="p-4 bg-white border-slate-200" data-testid="ps-scan-processed">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Processed</div>
            {processed && <Button size="sm" variant="outline" onClick={downloadImg} className="gap-1.5 text-xs h-8"><DownloadSimple size={12} /> PNG</Button>}
          </div>
          {processed ? (
            <img src={processed} alt="processed" className="w-full max-h-[350px] object-contain rounded-md bg-slate-50" data-testid="ps-processed-img" />
          ) : (
            <div className="w-full h-[350px] rounded-md border-2 border-dashed border-slate-200 flex items-center justify-center text-slate-400 text-sm">Auto-cropped &amp; deskewed image appears here</div>
          )}
        </Card>

        <Card className="md:col-span-2 p-4 bg-white border-slate-200" data-testid="ps-scan-text">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1.5"><TextT size={11} /> Extracted Text ({text ? text.length : 0} chars)</div>
            <div className="flex gap-1.5">
              {text && <Button size="sm" variant="outline" onClick={copyText} className="gap-1 text-xs h-8" data-testid="ps-copy-text"><Copy size={12} /> Copy</Button>}
              {pdf && <Button size="sm" variant="outline" onClick={downloadPdf} className="gap-1 text-xs h-8" data-testid="ps-download-pdf"><FilePdf size={12} /> Searchable PDF</Button>}
            </div>
          </div>
          <div className="min-h-[160px] max-h-[300px] overflow-y-auto rounded-md bg-slate-50 border border-slate-200 p-3 text-sm whitespace-pre-wrap font-mono text-slate-800" data-testid="ps-ocr-output">
            {text || <span className="text-slate-400">OCR text will appear here after scanning.</span>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
      <button type="button" onClick={() => onChange(!checked)} className="shrink-0" data-testid={testid}>
        {checked ? <CheckSquare size={16} weight="fill" className="text-orange-500" /> : <Square size={16} className="text-slate-400" />}
      </button>
      {label}
    </label>
  );
}
